interface LocaleConfig {
  defaultLocale?: string;
  fallbackLocale?: string;
  availableLocales?: string[];
  storageKey?: string;
  manageLocalePaths?: boolean;
}

declare global {
  interface Window {
    __GT_LOCALE_CONFIG__?: LocaleConfig;
  }
}

const globalScope = typeof window !== 'undefined' ? window : globalThis;
const localeConfig: LocaleConfig = (globalScope && (globalScope as { __GT_LOCALE_CONFIG__?: LocaleConfig }).__GT_LOCALE_CONFIG__) || {};

const configuredDefault =
  typeof localeConfig.defaultLocale === 'string' && localeConfig.defaultLocale.trim()
    ? localeConfig.defaultLocale.trim()
    : 'en';
const configuredFallback =
  typeof localeConfig.fallbackLocale === 'string' && localeConfig.fallbackLocale.trim()
    ? localeConfig.fallbackLocale.trim()
    : configuredDefault;
const configuredLocales =
  Array.isArray(localeConfig.availableLocales) && localeConfig.availableLocales.length
    ? localeConfig.availableLocales
        .map((locale) => (typeof locale === 'string' ? locale.trim() : ''))
        .filter(Boolean)
    : ['en', 'et'];

if (!configuredLocales.includes(configuredDefault)) {
  configuredLocales.unshift(configuredDefault);
}

const DEFAULT_LOCALE = configuredDefault;
const FALLBACK_LOCALE = configuredFallback;
const LOCALE_STORAGE_KEY =
  typeof localeConfig.storageKey === 'string' && localeConfig.storageKey.trim()
    ? localeConfig.storageKey.trim()
    : 'gt-locale';
const AVAILABLE_LOCALES = Array.from(new Set(configuredLocales));
const MANAGE_LOCALE_PATHS = localeConfig.manageLocalePaths !== false;

let translations: Record<string, unknown> = {};
let fallbackTranslations: Record<string, unknown> = {};
let currentLocale = DEFAULT_LOCALE;
let fallbackLoaded = false;
const loggedMissingKeys = new Set<string>();
const translationSubscribers = new Set<() => void>();
const localeSubscribers = new Set<(locale: string) => void>();

function lookup(source: Record<string, unknown> | undefined, path: string): unknown {
  if (!source) return undefined;
  const segments = path.split('.');
  let value: unknown = source;
  for (const segment of segments) {
    if (value && typeof value === 'object' && value !== null && segment in value) {
      value = (value as Record<string, unknown>)[segment];
    } else {
      return undefined;
    }
  }
  return value;
}

function logMissingKey(locale: string, key: string): void {
  const id = `${locale}:${key}`;
  if (loggedMissingKeys.has(id)) return;
  loggedMissingKeys.add(id);
  const suffix = locale === 'fallback' ? ' (fallback locale)' : '';
  console.warn(`[i18n] Missing translation key "${key}" for locale "${locale}"${suffix}.`);
}

function notifyTranslationsApplied() {
  translationSubscribers.forEach((fn) => {
    try {
      fn();
    } catch (err) {
      console.error('Translation subscriber failed', err);
    }
  });
}

function notifyLocaleChanged() {
  localeSubscribers.forEach((fn) => {
    try {
      fn(currentLocale);
    } catch (err) {
      console.error('Locale subscriber failed', err);
    }
  });
}

function translateSubtree(root: Document | Element | null) {
  const scope = root || document;
  const targets: HTMLElement[] = [];

  if (scope instanceof HTMLElement && scope.matches('[data-i18n]')) {
    targets.push(scope);
  }
  scope.querySelectorAll('[data-i18n]').forEach((el) => targets.push(el as HTMLElement));

  targets.forEach((el) => {
    const key = el.dataset.i18n;
    if (!key) return;
    const value = t(key);
    if (value) {
      el.innerHTML = value;
    }
  });

  const placeholderTargets: HTMLElement[] = [];
  if (scope instanceof HTMLElement && scope.matches('[data-i18n-placeholder]')) {
    placeholderTargets.push(scope);
  }
  scope
    .querySelectorAll('[data-i18n-placeholder]')
    .forEach((el) => placeholderTargets.push(el as HTMLElement));

  placeholderTargets.forEach((el: HTMLElement) => {
    const key = el.dataset.i18nPlaceholder;
    if (!key) return;
    const value = t(key);
    if (value) {
      el.setAttribute('placeholder', value);
    }
  });

  const ariaTargets: HTMLElement[] = [];
  if (scope instanceof HTMLElement && scope.matches('[data-i18n-aria-label]')) {
    ariaTargets.push(scope);
  }
  scope
    .querySelectorAll('[data-i18n-aria-label]')
    .forEach((el) => ariaTargets.push(el as HTMLElement));

  ariaTargets.forEach((el: HTMLElement) => {
    const key = el.dataset.i18nAriaLabel;
    if (!key) return;
    const value = t(key);
    if (value) {
      el.setAttribute('aria-label', value);
    }
  });

  const attrTargets: HTMLElement[] = [];
  if (scope instanceof HTMLElement && scope.matches('[data-i18n-attr]')) {
    attrTargets.push(scope);
  }
  scope.querySelectorAll('[data-i18n-attr]').forEach((el) => attrTargets.push(el as HTMLElement));

  attrTargets.forEach((el: HTMLElement) => {
    const mapping = el.dataset.i18nAttr;
    if (!mapping) return;
    mapping.split(';').forEach((entry: string) => {
      const parts = entry
        .split(':')
        .map((part: string) => part && part.trim())
        .filter(Boolean);
      const attr = parts[0];
      const key = parts[1];
      if (!attr || !key) return;
      const value = t(key);
      if (!value) return;
      if (attr === 'textContent') {
        el.textContent = value;
        return;
      }
      el.setAttribute(attr, value);
      if (attr.startsWith('data-')) {
        const dataKey = attr
          .slice(5)
          .split('-')
          .map((segment: string, index: number) =>
            index === 0 ? segment : segment.charAt(0).toUpperCase() + segment.slice(1)
          )
          .join('');
        if (dataKey) {
          (el.dataset as Record<string, string>)[dataKey] = value;
        }
      }
    });
  });
}

function applyTranslations() {
  document.documentElement.lang = currentLocale;
  translateSubtree(document);
  notifyTranslationsApplied();
}

function t(path: string, fallback: string = ''): string {
  if (!path) return fallback;
  const direct = lookup(translations, path);
  if (direct !== undefined) return typeof direct === 'string' ? direct : fallback;

  const fb = lookup(fallbackTranslations, path);
  if (fb !== undefined) {
    logMissingKey(currentLocale, path);
    return typeof fb === 'string' ? fb : fallback;
  }

  logMissingKey('fallback', path);
  return fallback;
}

function formatTemplate(template: string, values: Record<string, unknown>): string {
  if (typeof template !== 'string') return '';
  return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key) => {
    const val = values[key];
    return val !== undefined ? String(val) : match;
  });
}

function deriveLocaleFromPath() {
  const segments = (window.location.pathname || '').split('/').filter(Boolean);
  if (!segments.length) return null;
  const last = segments[segments.length - 1].toLowerCase();
  return AVAILABLE_LOCALES.includes(last) ? last : null;
}

function resolveContentUrl(locale: string): string {
  const directoryUrl = new URL('.', window.location.href);
  return new URL(`content/${locale}.json`, directoryUrl).href;
}

function resolveRequestedLocale(locale: string): string {
  if (locale && AVAILABLE_LOCALES.includes(locale)) {
    return locale;
  }
  if (locale && locale === FALLBACK_LOCALE) {
    return FALLBACK_LOCALE;
  }
  return DEFAULT_LOCALE;
}

function updateLocaleInUrl(locale: string): void {
  if (!MANAGE_LOCALE_PATHS) {
    return;
  }
  if (!window.history || typeof window.history.replaceState !== 'function') {
    return;
  }

  const url = new URL(window.location.href);
  const segments = url.pathname.split('/').filter(Boolean);
  const lastSegment = segments[segments.length - 1];
  const hasFileExtension =
    lastSegment && lastSegment.includes('.') && !AVAILABLE_LOCALES.includes(lastSegment);

  if (hasFileExtension) {
    return;
  }

  if (segments.length && AVAILABLE_LOCALES.includes(lastSegment)) {
    segments.pop();
  }

  if (locale !== DEFAULT_LOCALE) {
    segments.push(locale);
  }

  const hadTrailingSlash = url.pathname.endsWith('/') && url.pathname !== '/';
  let newPathname = segments.length ? `/${segments.join('/')}` : '/';
  if (hadTrailingSlash && !newPathname.endsWith('/')) {
    newPathname += '/';
  }

  if (newPathname === url.pathname) {
    return;
  }

  const newUrl = `${newPathname}${url.search}${url.hash}`;
  window.history.replaceState(null, '', newUrl);
}

async function loadLocaleData(
  locale: string
): Promise<{ data: Record<string, unknown>; locale: string }> {
  const candidate = resolveRequestedLocale(locale);
  const response = await fetch(resolveContentUrl(candidate), { cache: 'no-store' });
  if (!response.ok) throw new Error(`Failed to load locale: ${candidate}`);
  return { data: await response.json(), locale: candidate };
}

async function setLocale(locale: string): Promise<void> {
  const { data, locale: resolved } = await loadLocaleData(locale);
  translations = data;
  currentLocale = resolved;
  localStorage.setItem(LOCALE_STORAGE_KEY, currentLocale);
  updateLocaleInUrl(resolved);
  applyTranslations();
  notifyLocaleChanged();
}

async function initializeLocalization() {
  if (!fallbackLoaded) {
    const { data } = await loadLocaleData(FALLBACK_LOCALE);
    fallbackTranslations = data;
    fallbackLoaded = true;
  }

  const urlLocale = MANAGE_LOCALE_PATHS ? deriveLocaleFromPath() : null;
  const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
  const initial =
    urlLocale || (stored && AVAILABLE_LOCALES.includes(stored) ? stored : DEFAULT_LOCALE);
  await setLocale(initial);
}

function onTranslationsApplied(fn: () => void): void {
  if (typeof fn !== 'function') return;
  translationSubscribers.add(fn);
}

function onLocaleChanged(fn: (locale: string) => void): void {
  if (typeof fn !== 'function') return;
  localeSubscribers.add(fn);
}

function getCurrentLocale(): string {
  return currentLocale;
}

function getLocalizedArray(key: string, fallback: unknown[] = []): unknown[] {
  const direct = lookup(translations, key);
  if (Array.isArray(direct) && direct.length) {
    return direct.slice();
  }
  const fb = lookup(fallbackTranslations, key);
  if (Array.isArray(fb) && fb.length) {
    return fb.slice();
  }
  return Array.isArray(fallback) ? fallback.slice() : [];
}

export {
  AVAILABLE_LOCALES,
  DEFAULT_LOCALE,
  formatTemplate,
  getCurrentLocale,
  getLocalizedArray,
  initializeLocalization,
  onLocaleChanged,
  onTranslationsApplied,
  setLocale,
  translateSubtree,
  t,
};
